import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useUserPortfolio } from '../../hooks/contract/useUserPortfolio';
import { useAllPlans, FALLBACK_PLANS } from '../../hooks/contract/useAllPlans';
import { useApproveUsdt, useDeposit } from '../../hooks/contract/useWriteActions';
import { useReferralCode } from '../../contexts/ReferralCodeContext';
import { formatUsdt, parseUsdt, shortAddr, formatPlanLabel } from '../../lib/format';
import {
  MIN_STAKE_USDT,
  MIN_STAKE_WEI,
  PROTOCOL_FEE_BPS,
  BPS_DENOM,
} from '../../lib/constants';
import { isAddress } from 'viem';

const TIERS = ['starter', 'starter', 'standard', 'standard', 'premium', 'premium', 'elite'];
const QUICK = [50, 100, 500];

const NewDeposit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { usdtBalance, allowance, userInfo, refetch } = useUserPortfolio();
  const { data: onChainPlans } = useAllPlans();
  const plans = onChainPlans ?? FALLBACK_PLANS;

  const { refCode } = useReferralCode();
  const approveAction = useApproveUsdt();
  const depositAction = useDeposit();

  const [step, setStep] = useState(1);
  const [amount, setAmount] = useState('');
  const [planId, setPlanId] = useState(6); // default: 30d elite
  const [customSponsor, setCustomSponsor] = useState('');

  // Если у юзера уже есть sponsor on-chain — фиксирован, иначе — значение из refCode
  const hasOnChainSponsor =
    userInfo?.sponsor &&
    userInfo.sponsor !== '0x0000000000000000000000000000000000000000';
  const sponsorForTx = useMemo(() => {
    if (hasOnChainSponsor) return userInfo.sponsor;
    if (customSponsor && isAddress(customSponsor)) return customSponsor;
    if (refCode && isAddress(refCode)) return refCode;
    return '0x0000000000000000000000000000000000000000';
  }, [hasOnChainSponsor, userInfo?.sponsor, customSponsor, refCode]);

  const amountWei = amount && !Number.isNaN(Number(amount)) ? parseUsdt(amount) : 0n;
  const aboveMin = amountWei >= MIN_STAKE_WEI;
  const withinBalance = amountWei <= (usdtBalance ?? 0n);
  const needsApprove = amountWei > (allowance ?? 0n);

  const plan = plans[planId];
  const preview = useMemo(() => {
    if (!plan || amountWei === 0n) return null;
    const durSec = plan.durationSeconds
      ? Number(plan.durationSeconds)
      : Number(plan.durationDays) * 86400;
    const daily = (amountWei * BigInt(plan.dailyBps)) / BigInt(BPS_DENOM);
    const gross = (amountWei * BigInt(plan.dailyBps) * BigInt(durSec)) / (86400n * BigInt(BPS_DENOM));
    const fee = (gross * BigInt(PROTOCOL_FEE_BPS)) / BigInt(BPS_DENOM);
    const net = gross - fee;
    return { daily, gross, fee, net, isTest: Boolean(plan.isTest) };
  }, [plan, amountWei]);

  const canContinueStep1 = aboveMin && withinBalance;

  // Авто-перезагрузка после success
  useEffect(() => {
    if (depositAction.status === 'success') {
      refetch?.();
      const id = setTimeout(() => navigate('/dashboard/deposits'), 800);
      return () => clearTimeout(id);
    }
  }, [depositAction.status, navigate, refetch]);

  const handleApprove = async () => {
    await approveAction.approve();
    refetch?.();
  };

  const handleDeposit = async () => {
    if (!aboveMin || needsApprove) return;
    await depositAction.deposit({
      amount: amountWei,
      planId,
      referrer: sponsorForTx,
    });
  };

  const txBusy =
    approveAction.status === 'signing' ||
    approveAction.status === 'confirming' ||
    depositAction.status === 'signing' ||
    depositAction.status === 'confirming';

  return (
    <div className="db-page db-page--new">
      <div className="db-page__head">
        <h1 className="db-page__title">{t('dashboard.new.title')}</h1>
        <p className="db-page__subtitle">{t('dashboard.new.subtitle')}</p>
      </div>

      {/* Stepper */}
      <div className="step-bar">
        {[1, 2, 3].map((n) => (
          <div
            key={n}
            className={`step-bar__item${step === n ? ' is-current' : ''}${
              step > n ? ' is-done' : ''
            }`}
          >
            <span className="step-bar__num mono">{n}</span>
            <span className="step-bar__label">
              {t(`dashboard.new.step${n}.label`)}
            </span>
          </div>
        ))}
      </div>

      {/* STEP 1: Amount */}
      {step === 1 && (
        <div className="step-card">
          <h3>{t('dashboard.new.step1.title')}</h3>
          <div className="step-amount">
            <div className="step-amount__input-wrap">
              <input
                type="number"
                inputMode="decimal"
                className="step-amount__input mono tabular-nums"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                min={MIN_STAKE_USDT}
                step="0.01"
              />
              <span className="step-amount__unit">USDT</span>
            </div>
            <div className="step-amount__balance">
              {t('dashboard.new.step1.balance', {
                value: formatUsdt(usdtBalance ?? 0n),
              })}
            </div>
            <div className="step-amount__quick">
              {QUICK.map((q) => (
                <button
                  key={q}
                  type="button"
                  className="step-amount__chip"
                  onClick={() => setAmount(String(q))}
                >
                  {q}
                </button>
              ))}
              <button
                type="button"
                className="step-amount__chip step-amount__chip--max"
                onClick={() => setAmount(formatUsdt(usdtBalance ?? 0n, { maximumFractionDigits: 6, minimumFractionDigits: 0 }).replace(/,/g, ''))}
              >
                MAX
              </button>
            </div>
            {!aboveMin && amount && (
              <div className="step-hint step-hint--warn">
                <i className="fas fa-info-circle" />
                {t('dashboard.new.step1.belowMin', { min: MIN_STAKE_USDT })}
              </div>
            )}
            {aboveMin && !withinBalance && (
              <div className="step-hint step-hint--warn">
                <i className="fas fa-exclamation-triangle" />
                {t('dashboard.new.step1.insufficient')}
              </div>
            )}
          </div>
          <div className="step-actions">
            <button
              type="button"
              className="sf-btn sf-btn--gold"
              onClick={() => setStep(2)}
              disabled={!canContinueStep1}
            >
              {t('dashboard.new.next')} <i className="fas fa-arrow-right" />
            </button>
          </div>
        </div>
      )}

      {/* STEP 2: Plan */}
      {step === 2 && (
        <div className="step-card">
          <h3>{t('dashboard.new.step2.title')}</h3>
          <div className="step-plan-grid">
            {plans.map((p, i) => {
              const tier = p.isTest ? 'test' : TIERS[i] || 'starter';
              const active = planId === i;
              if (p.isTest) {
                const seconds = Number(p.durationSeconds ?? 600);
                const minutes = Math.round(seconds / 60);
                const periodPct = ((Number(p.dailyBps) * seconds) / 86400 / 100).toFixed(2);
                return (
                  <button
                    key={i}
                    type="button"
                    className={`step-plan step-plan--${tier}${active ? ' is-active' : ''}`}
                    onClick={() => setPlanId(i)}
                  >
                    <div className="step-plan__days mono">{minutes}</div>
                    <div className="step-plan__days-label">{t('dashboard.new.step2.minutes')}</div>
                    <div className="step-plan__daily gold-text tabular-nums">{periodPct}%</div>
                    <div className="step-plan__daily-label">
                      {t('dashboard.new.step2.perPeriod', { n: minutes })}
                    </div>
                    <div className="step-plan__total mono">
                      {t('dashboard.new.step2.testBadge', { minutes })}
                    </div>
                  </button>
                );
              }
              const apr = (Number(p.dailyBps) / 100) * Number(p.durationDays);
              return (
                <button
                  key={i}
                  type="button"
                  className={`step-plan step-plan--${tier}${active ? ' is-active' : ''}`}
                  onClick={() => setPlanId(i)}
                >
                  <div className="step-plan__days mono">{p.durationDays}</div>
                  <div className="step-plan__days-label">{t('dashboard.new.step2.days')}</div>
                  <div className="step-plan__daily gold-text tabular-nums">
                    {(Number(p.dailyBps) / 100).toFixed(2)}%
                  </div>
                  <div className="step-plan__daily-label">{t('dashboard.new.step2.perDay')}</div>
                  <div className="step-plan__total mono">{apr.toFixed(2)}% {t('dashboard.new.step2.total')}</div>
                </button>
              );
            })}
          </div>

          {preview && (
            <div className="step-preview">
              {!preview.isTest && (
                <div className="step-preview__row">
                  <span>{t('dashboard.new.step2.previewDaily')}</span>
                  <span className="mono tabular-nums">{formatUsdt(preview.daily)} USDT</span>
                </div>
              )}
              <div className="step-preview__row">
                <span>{t('dashboard.new.step2.previewGross')}</span>
                <span className="mono tabular-nums">{formatUsdt(preview.gross)} USDT</span>
              </div>
              <div className="step-preview__row">
                <span>{t('dashboard.new.step2.previewFee')}</span>
                <span className="mono tabular-nums">−{formatUsdt(preview.fee)} USDT</span>
              </div>
              <div className="step-preview__row step-preview__row--net">
                <span>{t('dashboard.new.step2.previewNet')}</span>
                <span className="mono tabular-nums gold-text">
                  {formatUsdt(preview.net)} USDT
                </span>
              </div>
            </div>
          )}

          <div className="step-actions">
            <button type="button" className="sf-btn sf-btn--outline" onClick={() => setStep(1)}>
              <i className="fas fa-arrow-left" /> {t('dashboard.new.back')}
            </button>
            <button type="button" className="sf-btn sf-btn--gold" onClick={() => setStep(3)}>
              {t('dashboard.new.next')} <i className="fas fa-arrow-right" />
            </button>
          </div>
        </div>
      )}

      {/* STEP 3: Review & Confirm */}
      {step === 3 && (
        <div className="step-card">
          <h3>{t('dashboard.new.step3.title')}</h3>

          <div className="step-review">
            <div className="step-review__row">
              <span>{t('dashboard.new.step3.amount')}</span>
              <span className="mono tabular-nums">{formatUsdt(amountWei)} USDT</span>
            </div>
            <div className="step-review__row">
              <span>{t('dashboard.new.step3.plan')}</span>
              <span>
                {plan
                  ? formatPlanLabel(plan, t)
                  : '—'}
              </span>
            </div>
            {preview && (
              <div className="step-review__row">
                <span>{t('dashboard.new.step3.netProfit')}</span>
                <span className="mono tabular-nums gold-text">
                  +{formatUsdt(preview.net)} USDT
                </span>
              </div>
            )}
            <div className="step-review__row">
              <span>{t('dashboard.new.step3.sponsor')}</span>
              <span className="mono">
                {hasOnChainSponsor
                  ? shortAddr(userInfo.sponsor)
                  : sponsorForTx === '0x0000000000000000000000000000000000000000'
                    ? t('dashboard.new.step3.noSponsor')
                    : shortAddr(sponsorForTx)}
              </span>
            </div>

            {!hasOnChainSponsor && (
              <div className="step-review__sponsor-input">
                <label>{t('dashboard.new.step3.sponsorOverride')}</label>
                <input
                  type="text"
                  className="mono"
                  placeholder="0x…"
                  value={customSponsor}
                  onChange={(e) => setCustomSponsor(e.target.value)}
                />
                {customSponsor && !isAddress(customSponsor) && (
                  <div className="step-hint step-hint--warn">
                    {t('dashboard.new.step3.invalidAddr')}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="step-lock-warning">
            <i className="fas fa-lock" />
            {plan?.isTest
              ? t('dashboard.new.step3.lockWarnMinutes', {
                  minutes: Math.round(Number(plan.durationSeconds ?? 600) / 60),
                })
              : t('dashboard.new.step3.lockWarn', { days: plan?.durationDays })}
          </div>

          <div className="step-actions">
            <button type="button" className="sf-btn sf-btn--outline" onClick={() => setStep(2)} disabled={txBusy}>
              <i className="fas fa-arrow-left" /> {t('dashboard.new.back')}
            </button>
            {needsApprove ? (
              <button
                type="button"
                className="sf-btn sf-btn--gold"
                onClick={handleApprove}
                disabled={txBusy}
              >
                {approveAction.status === 'signing' || approveAction.status === 'confirming'
                  ? t('dashboard.new.step3.approving')
                  : t('dashboard.new.step3.approve')}
              </button>
            ) : (
              <button
                type="button"
                className="sf-btn sf-btn--gold"
                onClick={handleDeposit}
                disabled={txBusy}
              >
                {depositAction.status === 'signing' || depositAction.status === 'confirming'
                  ? t('dashboard.new.step3.depositing')
                  : t('dashboard.new.step3.deposit')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NewDeposit;
